import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, MapPin, Users, Tag, ArrowLeft, Sparkles, CheckCircle2, ListChecks, Gift, Baby } from "lucide-react";
import { api } from "@/lib/api";
import ReservationDialog from "@/components/ReservationDialog";

const STAGE_IMG = "https://images.pexels.com/photos/8613089/pexels-photo-8613089.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1000";

export default function StageDetail() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/academy/${id}`).then((r) => setS(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-heading font-extrabold text-3xl text-slate-900">Stage introuvable</h1>
        <Link to="/academie" className="inline-block mt-6 text-sky-600 font-bold">Retour à L'Académie</Link>
      </div>
    );

  if (!s)
    return <div className="max-w-7xl mx-auto px-4 py-24"><div className="h-96 bg-slate-100 rounded-3xl animate-pulse" /></div>;

  const highlights = s.highlights || [];
  const program = s.program || [];
  const includes = s.includes || [];

  return (
    <div data-testid="stage-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <Link to="/academie" className="inline-flex items-center gap-2 text-slate-500 hover:text-amber-600 font-semibold text-sm transition-colors" data-testid="back-to-academy">
          <ArrowLeft className="w-4 h-4" /> Retour à L'Académie
        </Link>
      </div>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-2 gap-12">
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="relative">
          <div className="rounded-[2rem] overflow-hidden shadow-2xl shadow-amber-500/20">
            <img src={s.image || STAGE_IMG} alt={s.title} className="w-full h-[420px] object-cover" />
          </div>
          <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-bold bg-amber-100 text-amber-700">
            <Sparkles className="w-4 h-4" /> {s.period}
          </span>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600">Stage de vacances</span>
          <h1 className="font-heading font-black text-4xl lg:text-5xl text-slate-900 mt-2">{s.title}</h1>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">{s.description}</p>

          <div className="grid grid-cols-2 gap-3 mt-8">
            <InfoRow icon={Calendar} label="Dates" value={s.dates} />
            <InfoRow icon={Baby} label="Âges" value={s.ages} />
            <InfoRow icon={MapPin} label="Lieu" value={s.location} />
            <InfoRow icon={Users} label="Places" value={`${s.spots} enfants max.`} />
            <InfoRow icon={Tag} label="Tarif" value={`${s.price.toFixed(0)} € / enfant`} />
          </div>

          <div className="mt-8">
            <ReservationDialog
              kind="stage"
              itemId={s.id}
              itemTitle={s.title}
              trigger={
                <button data-testid="reserve-stage-button" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-amber-500 text-white font-bold text-lg hover:bg-amber-600 hover:scale-[1.02] transition-[transform,background-color]">
                  <Calendar className="w-5 h-5" /> Réserver une place
                </button>
              }
            />
          </div>
        </motion.div>
      </section>

      {/* Intro */}
      {s.intro && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-900 mb-4">Le programme de la semaine</h2>
          <p className="text-lg text-slate-700 leading-relaxed">{s.intro}</p>
        </section>
      )}

      {/* Points forts */}
      {highlights.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-gradient-to-br from-amber-400 to-amber-500 rounded-[2rem] p-8 sm:p-10 text-white">
            <h2 className="font-heading font-extrabold text-2xl sm:text-3xl mb-6 flex items-center gap-2">
              <Sparkles className="w-6 h-6" /> Points forts
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {highlights.map((h) => (
                <div key={h} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5 text-amber-50" />
                  <span className="text-amber-50">{h}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Déroulé + inclus */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid lg:grid-cols-2 gap-6">
        {program.length > 0 && (
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8">
            <h2 className="font-heading font-extrabold text-xl text-slate-900 flex items-center gap-2 mb-6">
              <ListChecks className="w-5 h-5 text-amber-500" /> Déroulé du stage
            </h2>
            <ol className="space-y-4">
              {program.map((p, i) => (
                <li key={p} className="flex gap-3">
                  <span className="grid place-items-center w-7 h-7 rounded-full bg-amber-100 text-amber-600 font-bold text-sm shrink-0">{i + 1}</span>
                  <span className="text-slate-600 text-sm pt-0.5">{p}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
        {includes.length > 0 && (
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8">
            <h2 className="font-heading font-extrabold text-xl text-slate-900 flex items-center gap-2 mb-6">
              <Gift className="w-5 h-5 text-emerald-500" /> Ce qui est inclus
            </h2>
            <ul className="space-y-3">
              {includes.map((inc) => (
                <li key={inc} className="flex items-start gap-3 text-slate-600 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> {inc}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* CTA final */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-slate-900 rounded-[2rem] p-8 sm:p-12 text-center">
          <h2 className="font-heading font-black text-3xl text-white">Inscrivez votre enfant au « {s.title} »</h2>
          <p className="text-slate-300 mt-3 max-w-xl mx-auto">Places limitées à {s.spots} enfants. Réservez dès maintenant, nous confirmons par email.</p>
          <div className="mt-8 flex justify-center">
            <ReservationDialog
              kind="stage"
              itemId={s.id}
              itemTitle={s.title}
              trigger={
                <button data-testid="reserve-stage-cta" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-amber-500 text-white font-bold text-lg hover:bg-amber-400 hover:scale-[1.02] transition-[transform,background-color]">
                  <Calendar className="w-5 h-5" /> Réserver une place
                </button>
              }
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex items-center gap-3">
      <span className="grid place-items-center w-11 h-11 rounded-xl bg-amber-100 text-amber-600 shrink-0">
        <Icon className="w-5 h-5" />
      </span>
      <div className="min-w-0">
        <p className="text-xs text-slate-400 font-semibold">{label}</p>
        <p className="font-heading font-extrabold text-slate-900 text-sm truncate">{value}</p>
      </div>
    </div>
  );
}
